/**
 * useOnboardingHydration - Syncs server-side onboarding state to localStorage
 *
 * Runs once per browser session when the user is authenticated.
 * Uses GET /profile/onboarding as the source of truth — server state wins.
 * This allows admins to reset/advance steps and the change takes effect
 * on the user's next session.
 *
 * Also flushes any locally completed steps that haven't reached the server yet
 * (e.g. completed offline or in a previous hook implementation).
 *
 * @atomic - single responsibility: one-time hydration (<80 lines)
 */

import { useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useAsyncOperation } from './useAsyncOperation'
import { onboardingAPI } from '../services/api/onboarding'
import { sightingAPI } from '../services/api/sightings'
import {
  setOnboardingProgressFromServer,
  markOnboardingStep,
  ONBOARDING_TRACK_KEY,
} from '../components/onboarding/OnboardingProgress'
import { createLogger } from '../utils/logger'

const logger = createLogger('OnboardingHydration')
const SESSION_KEY = 'onboarding_hydrated_session'

const useOnboardingHydration = () => {
  const { isAuthenticated, user } = useAuth()
  const { execute } = useAsyncOperation()

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return

    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') return
    } catch { /* sessionStorage unavailable */ }

    const hydrate = async () => {
      await execute(() => onboardingAPI.getMyState(), {
        onSuccess: async (response) => {
          const { track, completedSteps } = response.data ?? {}

          // 1. Write server steps as the canonical source so admin resets propagate.
          //    setOnboardingProgressFromServer replaces the local record (server wins)
          //    but preserves any local-only steps so they can be flushed back below.
          const previousLocal = completedSteps && typeof completedSteps === 'object'
            ? setOnboardingProgressFromServer(completedSteps)
            : {}

          // 2. Store track in localStorage so the component uses it without
          //    an extra fetch.
          if (track) {
            try {
              localStorage.setItem(ONBOARDING_TRACK_KEY, track)
              window.dispatchEvent(new CustomEvent('onboarding:track_changed', { detail: track }))
            } catch { /* noop */ }
          }

          // 3. If 'spotted' is absent from the server record, check the sightings
          //    table directly. This handles users who completed the action before
          //    the patchSteps call was wired up, or when that API call failed.
          const serverSteps = completedSteps ?? {}
          if (!serverSteps.spotted) {
            try {
              const statsRes = await sightingAPI.getUserStats(user.id)
              if (statsRes?.data?.totalSightings > 0) {
                const ts = Date.now()
                markOnboardingStep('spotted', ts)
                // Persist back to server so this check doesn't fire every session
                onboardingAPI.patchSteps({ spotted: ts }).catch(e =>
                  logger.warn('Could not persist auto-detected spotted step', e)
                )
              }
            } catch (e) {
              logger.warn('Could not auto-detect spotted step from sightings', e)
            }
          }

          // 4. Flush any locally completed steps the server doesn't have yet
          //    (e.g. steps completed before this migration was deployed).
          const missing = {}
          for (const [step, ts] of Object.entries(previousLocal)) {
            if (!serverSteps[step] && ts) missing[step] = ts
          }
          if (Object.keys(missing).length > 0) {
            onboardingAPI.patchSteps(missing).catch(e =>
              logger.warn('Could not flush local steps to server', e)
            )
          }

          try { sessionStorage.setItem(SESSION_KEY, '1') } catch { /* noop */ }
          logger.info('Onboarding hydrated from server', { track, completedSteps })
        },
        onError: (e) => {
          logger.warn('Onboarding hydration failed (will retry next session)', e)
        }
      })
    }

    hydrate()
  }, [isAuthenticated, user?.id, execute])
}

export default useOnboardingHydration
